import {Injectable} from '@angular/core';
import {EMPTY, forkJoin, Observable, of} from 'rxjs';
import {MatSelectChange} from '@angular/material';
import {map} from 'rxjs/internal/operators';
import {BaseService} from './base.service';
import {AuthService} from './auth.service';
import {MarketLevel} from '../models/market-level';
import {Market} from '../models/market';
import {User} from '../models/user';
import {base_urls} from '../config/base_urls';
import {MarketRef} from '../models/market-ref';

@Injectable({providedIn: 'root'})
export class MarketService {

    loggedUser: User;
    marketLevels: MarketLevel[] = [];
    markets = [];
    selectedMarket: Market[] = [];
    userRank: number;
    userMarketHierarchyLength = 0;
    filteredMarketLevels: MarketLevel[] = [];
    marketLevelFilterId = null;
    marketLevel = new MarketLevel();
    constructor(private auth: AuthService, private service: BaseService) {
        this.loggedUser = this.auth.user;
        this.marketLevels = this.loggedUser.MarketLevelHierarchy;
        if (this.marketLevels.length > 0) {
            this.marketLevels.forEach(pMarketLevel => {
                this.markets[pMarketLevel.Rank] = [];
            });
            if (this.loggedUser.UserRole.MarketLevel) {
                this.userRank = this.loggedUser.UserRole.MarketLevel.Rank;
            } else {
                this.userRank = 0;
            }
            this.userMarketHierarchyLength = this.loggedUser.MarketLevelHierarchy.length;
            if (this.userRank > 0) {
                if (this.userMarketHierarchyLength != this.userRank) {
                    this.marketLevelFilterId = this.marketLevels[this.userRank].Id;
                    this.marketLevel = this.marketLevels[this.userRank];
                } else {
                    this.marketLevelFilterId = this.marketLevels[this.userRank - 1].Id;
                    this.marketLevel = this.marketLevels[this.userRank - 1];
                }
            } else {
                this.marketLevelFilterId = this.marketLevels[0].Id;
                this.marketLevel = this.marketLevels[0];
            }
        }
    }

    prepareUserMarket(): Market[] {
        if (this.marketLevels) {
            this.marketLevels.forEach(pMarketLevel => {
                this.selectedMarket[pMarketLevel.Rank] = null;
            });
        }
        if (this.loggedUser.UserMarketList) {
            if (this.loggedUser.UserMarketList.length == 1) {
                this.loggedUser.UserMarketList.forEach(pMarket => {
                    if (pMarket.Market.ParentMarketList) {
                        pMarket.Market.ParentMarketList.forEach(pParentMarket => {
                            this.selectedMarket [pParentMarket.MarketLevel.Rank] = pParentMarket;
                        });
                    }
                    this.selectedMarket[this.userRank] = this.loggedUser.UserMarketList[0].Market;
                });
            }
        } else if (this.loggedUser.MarketList) {
            if (this.loggedUser.MarketList.length == 1) {
                this.loggedUser.MarketList.forEach(pMarket => {
                    if (pMarket.ParentMarketList) {
                        pMarket.ParentMarketList.forEach(pParentMarket => {
                            this.selectedMarket [pParentMarket.MarketLevel.Rank] = pParentMarket;
                        });
                    }
                    this.selectedMarket[this.userRank] = this.loggedUser.MarketList[0];
                });
            }
        }
        return this.selectedMarket;
    }

    marketChangeListener(pEvent: MatSelectChange, pMarketLevelRank: number, currentSelectedMarkets: Market[]): Observable<Market[]> {
        const selectedMarkets = [];
        this.marketLevels.forEach(pMarketLevel => {
            selectedMarkets[pMarketLevel.Rank] = null;
        });
        let rank = 0;

        if (pEvent.value) {
            return this.service.getMarketDetails(pEvent.value.id).pipe(
                map(pMarket => {
                    if (pMarket.ParentMarketList) {
                        for (let i = pMarket.ParentMarketList.length - 1; i >= 0; i--) {
                            selectedMarkets[pMarket.ParentMarketList[i].MarketLevel.Rank] = pMarket.ParentMarketList[i];
                            rank = pMarket.ParentMarketList[i].MarketLevel.Rank;
                        }
                        selectedMarkets[rank + 1] = pMarket;
                    } else {
                        for (let i = 0; i < selectedMarkets.length; i++) {
                            if (pMarket.MarketLevel.Rank == i) {
                                selectedMarkets[i] = pMarket;
                            }
                        }
                    }
                    return selectedMarkets;
                })
            );
        } else {
            for (let i = pMarketLevelRank; i <= this.marketLevels.length; i++) {
                currentSelectedMarkets[i] = null;
            }
            if (this.userRank > 1) {
                let selectedMarket = new Market();
                for (let i = currentSelectedMarkets.length - 1; i >= 0; i--) {
                    if (currentSelectedMarkets[i]) {
                        selectedMarket = currentSelectedMarkets[i];
                        break;
                    }
                }
                if (selectedMarket.MarketLevel) {
                    if (selectedMarket.MarketLevel.Rank < this.userRank) {
                        for (let i = 0; i < currentSelectedMarkets.length; i++) {
                            currentSelectedMarkets[i] = null;
                        }
                    }
                }
            }
            return of(currentSelectedMarkets);
        }
    }

    digDownMarket(marketId: number): Observable<any> {
        const selectedMarkets = [];
        this.marketLevels.forEach(pMarketLevel => {
            selectedMarkets[pMarketLevel.Rank] = null;
        });
        let rank = 0;

        return this.service.getMarketDetails(marketId).pipe(
            map(pMarket => {
                if (pMarket.ParentMarketList) {
                    for (let i = pMarket.ParentMarketList.length - 1; i >= 0; i--) {
                        selectedMarkets[pMarket.ParentMarketList[i].MarketLevel.Rank] = pMarket.ParentMarketList[i];
                        rank = pMarket.ParentMarketList[i].MarketLevel.Rank;
                    }
                    selectedMarkets[rank + 1] = pMarket;
                } else {
                    for (let i = 0; i < selectedMarkets.length; i++) {
                        if (pMarket.MarketLevel.Rank == i) {
                            selectedMarkets[i] = pMarket;
                        }
                    }
                }
                return {
                    selectedMarkets,
                    nextLevelRank: pMarket.MarketLevel.Rank
                };
            })
        );
    }

    prepareMarket(pMarketId: number): Observable<Market[]> {
        const selectedMarkets = [];
        this.marketLevels.forEach(pMarketLevel => {
            selectedMarkets[pMarketLevel.Rank] = null;
        });
        let rank = 0;

        return this.service.getMarketDetails(pMarketId).pipe(
            map(pMarket => {
                if (pMarket.ParentMarketList) {
                    for (let i = pMarket.ParentMarketList.length - 1; i >= 0; i--) {
                        selectedMarkets[pMarket.ParentMarketList[i].MarketLevel.Rank] = pMarket.ParentMarketList[i];
                        rank = pMarket.ParentMarketList[i].MarketLevel.Rank;
                    }
                    selectedMarkets[rank + 1] = pMarket;
                } else {
                    for (let i = 0; i < selectedMarkets.length; i++) {
                        if (pMarket.MarketLevel.Rank == i) {
                            selectedMarkets[i] = pMarket;
                        }
                    }
                }
                return selectedMarkets;
            })
        );
    }

    getSelectedLevelMarkets(marketLevel: MarketLevel, currentSelectedMarkets: Market[]): Observable<MarketRef[]> {
        const marketLevelId = marketLevel.Id;
        let marketId = null;
        let market = new Market();

        for (let i = currentSelectedMarkets.length - 1; i >= 0; i--) {
            if (currentSelectedMarkets[i]) {
                marketId = currentSelectedMarkets[i].Id;
                market = currentSelectedMarkets[i];
                break;
            }
        }

        if (marketId && marketLevelId != market.MarketLevel.Id && marketLevel.Rank > market.MarketLevel.Rank) {

            return this.service.getMarketListMicroService({
                pageNumber: 0,
                recordsPerPage: null,
                marketLevelId: marketLevelId,
                parentMarketId: marketId,
                active: true
            }, base_urls.f_base);
        } else {
            return EMPTY;
        }
    }

    getSelectedMarketList(pSelectedMarket: Market[]): Observable<Market[]> {
        const observables = [];
        for (let i = 1; i < pSelectedMarket.length; i++) {
            if (pSelectedMarket[i]) {
                const marketLevelId = pSelectedMarket[i].MarketLevel.Id;
                let marketId;
                if (i == 1 || pSelectedMarket[i].MarketLevel.Rank <= this.userRank) {
                    marketId = null;
                } else {
                    marketId = pSelectedMarket[i - 1].Id;
                }
                observables.push(this.service.getMarketListMicroService({
                    pageNumber: 0,
                    recordsPerPage: null,
                    marketLevelId: marketLevelId,
                    parentMarketId: null,
                    active: true
                }, base_urls.f_base));
            } else {
                const marketLevelId = this.loggedUser.MarketLevelHierarchy[i - 1].Id;
                const marketId = pSelectedMarket[i - 1] ? pSelectedMarket[i - 1].Id : null;
                observables.push(this.service.getMarketListMicroService({
                    pageNumber: 0,
                    recordsPerPage: null,
                    marketLevelId: marketLevelId,
                    parentMarketId: null,
                    active: true
                }, base_urls.f_base));
            }
        }
        return forkJoin(observables).pipe(
            map(responses => {
                responses.forEach((pResponse, index) => {
                    if (pSelectedMarket[index + 1]) {
                        this.markets[pSelectedMarket[index + 1].MarketLevel.Rank] = pResponse;
                    } else {
                        this.markets[this.loggedUser.MarketLevelHierarchy[index].Rank] = pResponse;
                    }
                });
                return this.markets;
            })
        );
    }

    getInitialMarketList(): Observable<any> {
        const observables = this.marketLevels.map((pMarketLevel) =>
                this.service.getMarketListMicroService({
                    pageNumber: 0,
                    recordsPerPage: null,
                    marketLevelId: pMarketLevel.Id,
                    parentMarketId: null,
                    active: true
                }, base_urls.f_base)

        );
        return forkJoin(observables);
    }
}
